import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import IssueDetailModal from '../../components/IssueDetailModal';
import CreateIssueModal from '../../components/CreateIssueModal';
import ConstructionBreadcrumb from '../../components/ConstructionBreadcrumb';
import ConstructionContextFilter from '../../components/ConstructionContextFilter';
import { matchesContextFilter } from '../../lib/constructionCache';
import { Plus, Loader2, ArrowRight, ArrowLeft, Calendar, User as UserIcon, ClipboardList } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLUMNS = [
  { key: 'open', label: 'Open', color: 'border-t-blue-500' },
  { key: 'in_progress', label: 'In Progress', color: 'border-t-amber-500' },
  { key: 'resolved', label: 'Resolved', color: 'border-t-green-500' },
];
const NEXT = { open: 'in_progress', in_progress: 'resolved', resolved: 'closed' };
const PREV = { in_progress: 'open', resolved: 'in_progress' };
const PRIORITY_STYLE = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-slate-100 text-slate-600' };

const Issues = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [showClosed, setShowClosed] = useState(false);
  const [ctxFilter, setCtxFilter] = useState({});
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchIssues = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/issues`);
      setIssues(res.data || []);
    } catch (e) { /* handled by interceptor */ } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => {
    axios.get(`${API}/projects/${projectId}`).then((r) => setProject(r.data)).catch(() => {});
    fetchIssues();
  }, [projectId, fetchIssues]);

  const move = async (issue, toStatus) => {
    setIssues((prev) => prev.map((i) => (i.id === issue.id ? { ...i, status: toStatus } : i)));
    try {
      await axios.patch(`${API}/issues/${issue.id}`, { status: toStatus });
    } catch (e) {
      fetchIssues();
    }
  };

  const byStatus = (s) => issues.filter((i) => i.status === s && matchesContextFilter(i.constructionContext, ctxFilter));
  const closed = byStatus('closed');

  const Card = ({ issue }) => (
    <div
      className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => setDetailId(issue.id)}
      data-testid="issue-card"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-slate-900 line-clamp-2">{issue.title}</p>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PRIORITY_STYLE[issue.priority] || PRIORITY_STYLE.medium}`}>{issue.priority || 'medium'}</span>
      </div>
      <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500 flex-wrap">
        {issue.assignedToName ? (
          <span className="flex items-center gap-1"><UserIcon className="w-3 h-3" />{issue.assignedToName}</span>
        ) : <span className="text-slate-400">Unassigned</span>}
        {issue.dueDate && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{issue.dueDate.slice(0, 10)}</span>}
        {(issue.comments || []).length > 0 && <span>💬 {issue.comments.length}</span>}
        {issue.sourceJobId && <span className="text-teal-600">from job</span>}
      </div>
      <div className="mt-2"><ConstructionBreadcrumb projectId={projectId} context={issue.constructionContext} fallback="Project level" testId={`issue-ctx-${issue.id}`} /></div>
      <div className="flex justify-between mt-2" onClick={(e) => e.stopPropagation()}>
        {PREV[issue.status] ? (
          <button onClick={() => move(issue, PREV[issue.status])} className="text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-0.5" data-testid="issue-move-prev"><ArrowLeft className="w-3 h-3" /> Back</button>
        ) : <span />}
        {NEXT[issue.status] && (
          <button onClick={() => move(issue, NEXT[issue.status])} className="text-[11px] text-amber-600 hover:text-amber-800 flex items-center gap-0.5 ml-auto" data-testid="issue-move-next">
            {NEXT[issue.status] === 'closed' ? 'Close' : 'Advance'} <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><ClipboardList className="w-7 h-7 text-amber-500" /> Punch List</h1>
            <p className="text-slate-600 mt-1">{project?.name} · {issues.length} issues</p>
          </div>
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2" data-testid="new-issue-btn">
            <Plus className="w-4 h-4" /> New Issue
          </button>
        </div>

        <ConstructionContextFilter projectId={projectId} value={ctxFilter} onChange={setCtxFilter} testId="issues-context-filter" />

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading issues…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="issue-board">
              {COLUMNS.map((col) => (
                <div key={col.key} className={`bg-slate-50 rounded-xl border-t-4 ${col.color} border border-slate-200 p-3`} data-testid={`column-${col.key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-800">{col.label}</h3>
                    <span className="text-xs bg-white px-2 py-0.5 rounded-full border border-slate-200">{byStatus(col.key).length}</span>
                  </div>
                  <div className="space-y-3 min-h-[120px]">
                    {byStatus(col.key).length === 0 && <p className="text-xs text-slate-400 text-center py-6">No issues</p>}
                    {byStatus(col.key).map((issue) => <Card key={issue.id} issue={issue} />)}
                  </div>
                </div>
              ))}
            </div>

            {closed.length > 0 && (
              <div>
                <button onClick={() => setShowClosed((s) => !s)} className="text-sm text-slate-500 hover:text-slate-800" data-testid="toggle-closed">
                  {showClosed ? 'Hide' : 'Show'} closed ({closed.length})
                </button>
                {showClosed && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3 opacity-75" data-testid="column-closed">
                    {closed.map((issue) => <Card key={issue.id} issue={issue} />)}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <CreateIssueModal
        projectId={projectId}
        job={null}
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => fetchIssues()}
      />
      <IssueDetailModal
        issueId={detailId}
        currentUser={currentUser}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        onChanged={() => fetchIssues()}
      />
    </Layout>
  );
};

export default Issues;
